<template>
  <div class="controls">
    <div class="controls__start">
      <span v-if="isLoading" class="controls__count">Experiments</span>
      <span v-else class="controls__count">{{ `Experiments (${experiments.length})` }}</span>
    </div>

    <HelpTip>
      Model experiment contains training sessions and validate sessions.
      The experiments allow user to compare the results of all sessions within.
      Click on Training Sessions or Validate Sessions to navigate to the corresponding dashboard.
    </HelpTip>

    <HelpTip v-if="permissions && !permissions.trainer_write" class="ml-1" help_style="alert">
      This is a read only experiment. Editing functionalities are disabled.
      To train a model using datasets in this project, create a local project, copy datasets to the local project and train using copied datasets.
    </HelpTip>

    <div class="controls__end">
      <div class="controls__actions">
        <button
          v-if="permissions && permissions.trainer_write"
          id="new-experiment-btn"
          class="button button-sm"
          @click="openCreateModal"
        >
          New Experiment
        </button>
      </div>
    </div>
  </div>
  <div v-if="isLoading" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  <div v-else-if="!isLoading && experiments.length === 0" class="empty-state">
    <EmptyState
      :title="'No experiments found for this project'"
      :subtitle="'To add a new experiment, click on NEW EXPERIMENT'"
    />
  </div>
  <div v-else-if="!isLoading && experiments.length > 0" class="manager-card-container scrollbar">
    <ExperimentCard
      v-for="e in experiments.sort((a,b) => b.id - a.id)"
      :id="e.id"
      :key="e.id"
      :name="e.name"
      :description="e.description"
      :running="e.no_running"
      :complete="e.no_completed"
      :terminated="e.no_terminated"
      :valRunning="e.val_running"
      :valError="e.val_error"
      :valCompleted="e.val_completed"
      :valTerminated="e.val_terminated"
      :errors="e.no_error"
      @delete-experiment="openConfirmDeleteTrainerModal(e)"
      @train-clicked="handleTrainClicked(e)"
      @val-clicked="handleValClicked(e)"
      @edit-experiment="openEditModal(e)"
    />
  </div>

  <CreateTrainerModal
    ref="createEditTrainerModal"
    :project="currentProject"
    @trainer-created="handleTrainerCreated"
  />
  <TrainerUpdateModal
    ref="trainerUpdateModal"
    :modalTrainer="modalTrainer"
    @edit-trainer="handleUpdateTrainer"
  />

  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import ExperimentCard from '@/components/ExperimentComponents/ExperimentCard.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import ConfirmModal from '@/components/ConfirmModal.vue';
import EmptyState from '@/components/EmptyState.vue';
import TrainerUpdateModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/TrainerUpdateModal.vue';
import CreateTrainerModal from '@/components/TrainerComponent/TrainerExperimentAndSessions/CreateTrainerModal.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import HelpTip from '@/components/HelpTip.vue';
import { decodeID, encodeID } from '@/assets/js/utils.js';
import { goto_trainers, goto_validations } from '@/assets/js/links.js';

const $toast = useToast();
const router = useRouter();
const route = useRoute();
const store = useStore();

// Props
defineProps({
  projectId: [String, Number],
});

// Emits
defineEmits(['trainer-set-created', 'update-list']);

// Refs
const experiments = ref([]);
const isLoading = ref(true);
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const toDelete = ref(null);
const confirmFunction = ref(null);
const buttonText = ref('Delete');
const modalTrainer = ref(null);
const permissions = ref(null);
const datasetList = ref([]);
const loadingFinished = ref(false);
const createEditTrainerModal = ref(null);
const trainerUpdateModal = ref(null);
const confirmModal = ref(null);

// Data connect
let dataConnect = null;

// Computed
const currentProject = computed(() => store.state.projects.currentProject);

const isPublic = computed(() => route.name?.startsWith('public.') || false);

const currentExperiment = computed({
  get() {
    return store.state.experiments.currentExperiment;
  },
  set(experiment) {
    store.commit('experiments/setCurrentExperiment', experiment);
  },
});

const notEmpty = computed(() => experiments.value && experiments.value.length > 0);

const trainerIds = computed(() => 
  experiments.value.reduce((acc, curr) => {
    acc.push(curr.id);
    return acc;
  }, [])
);

// Methods
const getProjectIdFromRoute = () => {
  const routeProjectId = route.params.projectId;
  if (!routeProjectId) return null;
  
  // Try to decode if it's UID format (p-xxx), otherwise use as decimal ID
  const decimalId = decodeID(routeProjectId) || parseInt(routeProjectId, 10);
  return decimalId;
};

// const loadDSList = async () => {
//   const currentProj = store.state.projects.currentProject;
//   if (currentProj) {
//     const params = {
//       project_id: currentProject.value.id,
//       get_annotation_sets: true,
//       get_groups: true,
//       get_labels: true,
//       get_parked: false,
//     };

//     await dataConnect.getDatasetList(params)
//       .then((resp) => {
//         if (!resp.error) {
//           datasetList.value = resp.result;
//           store.commit('datasets/setDatasetList', datasetList.value);
//         } else {
//           throw Error(resp.error.message);
//         }
//       })
//       .catch((error) => {
//         $toast.open({
//           queue: true,
//           dismissible: true,
//           message: `Failed to get details for datasets: ${error}`,
//           type: "error",
//           position: "top",
//         });
//       });
//   }
// };

const getExperimentList = async () => {
  isLoading.value = true;
  const projectId = getProjectIdFromRoute();
  
  experiments.value = [];
  if (!projectId) {
    console.warn('Project ID not found in route');
  }
  
  const be = new DatastoreConnect();
  const resp = await be.getExperimentList({ project_id: projectId })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiments: ${error}`,
        type: "error",
        position: "top",
      });
    });
  if (!resp || resp.error || !resp.result) {
    isLoading.value = false;
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to retrieve experiments: ${resp.error.message}`,
      type: "error",
      position: "top",
    });

  }
  isLoading.value = false;
  experiments.value = resp.result;
};

// const loadExpList = async () => {
//   isLoading.value = true;
//   experiments.value = await getTrainerList();
//   if (experiments.value.length > 0) {
//     experiments.value = experiments.value.sort((a, b) => b.id - a.id);
//   }
//   isLoading.value = false;
// };

const handleTrainClicked = (exp) => {
  currentExperiment.value = exp;
  goto_trainers(currentProject.value.id, exp.id);
};

const handleValClicked = (exp) => {
  currentExperiment.value = exp;
  goto_validations(currentProject.value.id, exp.id);
};

const getProjectAccessForSingleUser = async () => {
  if (currentProject.value && store.state.user?.user?.username) {
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentProject.value.id,
      username: store.state.user.user.username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        }
      })
      .catch((e) => console.log(e));
  }
};

const openConfirmDeleteTrainerModal = (t) => {
  confirmMessage.value = `Are you sure you want to remove experiment "${t.name}" to recycle bin? This will also remove all training sessions and validate sessions in progress.`;
  confirmMessageHeader.value = 'Remove Trainer Experiment';
  toDelete.value = t;
  buttonText.value = 'Remove';
  confirmModal.value.showModal();
  confirmFunction.value = () => handleTrainerDelete();
};

const handleTrainerCreated = async () => {
  experiments.value = await getTrainerList();
};

const handleTrainerDelete = async () => {
  await dataConnect.deleteTrainer({
    trainer_id: toDelete.value.id,
  })
    .then((data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete experiment: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to delete experiment: ${error}`,
        type: "error",
        position: "top",
      });
    });

  experiments.value = await getTrainerList();
  toDelete.value = null;
};

const handleUpdateTrainer = async (params) => {
  await dataConnect.updateTrainer(params)
    .then(async (data) => {
      if (data.result) {
        experiments.value = await getTrainerList();
      } else if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to update experiment: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to update experiment: ${error}`,
        type: "error",
        position: "top",
      });
    });
};

const openCreateModal = () => {
  createEditTrainerModal.value.showModal();
};

const openEditModal = (e) => {
  modalTrainer.value = e;
  trainerUpdateModal.value.showModal();
};

// Watchers
watch(() => route.params.projectId, async (newProjectId) => {
  if (newProjectId) {
    await getExperimentList();
  }
});

// Lifecycle
onMounted(() => {
  getExperimentList();
});
</script>

<style lang="scss" scoped>

.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.trainers-card-container {
  display: flex;
  width: 100%;
  padding: 16px;
  overflow-y: scroll;
  flex-wrap: wrap;
}

.trainer-header {
  display: flex;
  flex-direction: row;
  gap: 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}

//======================================
.manager-card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-auto-rows: 400px;
  width: 100%;
  gap: 16px;
  padding: 16px;
  overflow-y: auto;
  .list-card {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }
}
</style>
